import React, { useEffect, useState } from 'react';
import { Row, Col, Button, CardText } from 'reactstrap';
import { Control, Field } from 'react-redux-form';
import { FormGroup } from 'components/common';
import { Trans } from 'react-i18next';
import Select from 'react-select';
import { hasBillingAddressData, validateNumericField, validateVatNumber } from './validationUtils';

// Re-export for backward compatibility
export { hasBillingAddressData };

const BillingContactForm = ({
  addNewContact,
  setAddNewContact,
  billingData,
  setBillingAddressId,
  billingAddressId,
  countryOptions,
  t,
  colStyle,
  checkboxStyle,
  styleCheck,
  onChangeHandler = () => {},
  onCountryChangeHandler = () => {},
}) => {
  const [currentCountry, setCurrentCountry] = useState(null);
  
  const hasCompleteDefaultAddress = billingData?.default_address && 
    hasBillingAddressData(billingData.default_address).isValid;

  useEffect(() => {
    if (!addNewContact && hasCompleteDefaultAddress) {
      onChangeHandler({
        billing_address: billingData.default_address.id,
        billing_address_data: null
      })

    } else { 
      onChangeHandler({
        billing_address: null,
        billing_address_data: {}
      })
      
    }
    
    onCountryChangeHandler(null);

  }, [addNewContact, billingData]);

  // Validation functions are imported from validationUtils

  const handleFormChange = (field, value) => {

    if (!addNewContact) return;

    let validatedValue = value;
    
    // Update current country if country field is changed
    if (field === 'country') {
      setCurrentCountry(value);
      onCountryChangeHandler(value);
    }
    
    // Apply validation based on field type and country
    if (field === 'building_number' || field === 'plot_number') {
      validatedValue = validateNumericField(value, 4, currentCountry);
    } else if (field === 'postal_code') {
      validatedValue = validateNumericField(value, 5, currentCountry);
    } else if (field === 'vat_number') {
      validatedValue = validateVatNumber(value, currentCountry);
    }

    onChangeHandler(prevData => ({
      // billing_address: null,
      billing_address_data: {
        ...(prevData?.billing_address_data || {}),
        [field]: validatedValue
      }
    }));
  };

  return (
    <>
      <h4 className="mb-3">{ t('Billing Contact') }</h4>
      <Row>
        <Col>
          {!addNewContact && (
            <Button 
              className="btn btn-success float-right" 
              type="button" 
              onClick={() => { 
                setAddNewContact(true); 
                setBillingAddressId(null); 
              }}
            >
              {t(`Add New Contact`)}
            </Button>
          )}

          {(addNewContact && hasCompleteDefaultAddress) && (
            <Button 
              className="btn btn-success float-right" 
              type="button" 
              onClick={() => setAddNewContact(false)}
            >
              {t(`Use Default Contact`)}
            </Button>
          )}
        </Col>
      </Row>

      {(hasCompleteDefaultAddress && !addNewContact) ? (
        <Row>
          <Col>
            <div className="d-flex flext-direction-row">
              <div className="mr-2">
                <Field 
                  id="billing_address_id"
                  model='.billing_address'
                  onClick={(evt) => {
                    setBillingAddressId(evt.target.value);
                    handleFormChange('billing_address', evt.target.value);
                  }}
                  component="input"
                  type="radio"
                  checked={!!billingAddressId}
                  value={billingData?.default_address?.id}
                />
              </div>
              <label htmlFor="billing_address_id">
                <CardText className="mb-0">{billingData?.default_address?.first_name}</CardText>
                <CardText className="mb-0">{billingData?.default_address?.email}</CardText>
                <CardText className="mb-0">{billingData?.default_address?.contact_number}</CardText>
                <CardText className="mb-0">
                  <span>
                    {billingData?.default_address?.address_first_line}, 
                    {billingData?.default_address?.city}, 
                    {billingData?.default_address?.region}, 
                    {billingData?.default_address?.country}, 
                    {billingData?.default_address?.postal_code}
                  </span>
                </CardText>
              </label>
            </div>
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col>
              <FormGroup model=".first_name">
                <div className="cta-radio-gr">
                  <div className="d-flex my-1">
                    <Control.checkbox 
                      {...checkboxStyle}
                      model=".billing_address_data.is_default"
                      defaultChecked={true}
                      onChange={(e) => handleFormChange('is_default', e.target.checked)}
                    />
                    <span {...styleCheck}><Trans>Save as default contact</Trans></span>
                  </div>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col {...colStyle}>
              <FormGroup label={t("Address Person Name *")} model=".billing_address_data.first_name">
                <Control.text
                  updateOn="blur"
                  placeholder={t("Address Person Name")}
                  className="mb-2"
                  model=".billing_address_data.first_name"
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_first_name_field"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col {...colStyle}>
              <FormGroup label={t("Email *")}>
                <Control.text
                  model=".billing_address_data.email"
                  className="rounded"
                  placeholder={t("Email")}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_email_field"
                />
              </FormGroup>
            </Col>
            <Col {...colStyle}>
              <FormGroup label={t("Contact Number *")} model=".billing_address_data.contact_number">
                <Control.text
                  model=".billing_address_data.contact_number"
                  className="rounded"
                  placeholder={t("Contact Number")}
                  onChange={(e) => handleFormChange('contact_number', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_contact_number_field"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Country - Full width for better visibility */}
          <Row>
            <Col>
              <FormGroup label={t("Country *")} model=".billing_address_data.country" addControlClass={false}>
                <Control.text
                  style={{ backgroundColor: '#F4F7FE' }}
                  model=".billing_address_data.country"
                  component={Select}
                  options={countryOptions}
                  simpleValue
                  clearable={false}
                  searchable={true}
                  placeholder={t('Select...')}
                  onChange={(option) => handleFormChange('country', option)}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Address Line - Full width for complete address */}
          <Row>
            <Col>
              <FormGroup label={t("Address Line *")} model=".billing_address_data.address_first_line">
                <Control.text 
                  model=".billing_address_data.address_first_line" 
                  className="mb-2" 
                  placeholder={t("Address Line")}
                  onChange={(e) => handleFormChange('address_first_line', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_address_line_field"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Building and Plot Numbers - Related specific details */}
          <Row>
            <Col {...colStyle}>
              <FormGroup label={currentCountry === 'SA' ? t("Building Number *") : t("Building Number")} model=".billing_address_data.building_number">
                <Control.text 
                  maxLength={currentCountry === 'SA' ? "4" : "20"} 
                  model=".billing_address_data.building_number" 
                  placeholder={currentCountry === 'SA' ? t("Building Number (4 digits)") : t("Building Number")} 
                  className="rounded"
                  onChange={(e) => handleFormChange('building_number', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_building_number_field"
                />
                {currentCountry === 'SA' && (
                  <small className="text-muted">{t("Must be exactly 4 digits for Saudi Arabia")}</small>
                )}
              </FormGroup>
            </Col>
            <Col {...colStyle}>
              <FormGroup label={currentCountry === 'SA' ? t("Plot Number *") : t("Plot Number")} model=".billing_address_data.plot_number">
                <Control.text 
                  maxLength={currentCountry === 'SA' ? "4" : "20"} 
                  model=".billing_address_data.plot_number" 
                  placeholder={currentCountry === 'SA' ? t("Plot Number (4 digits)") : t("Plot Number")} 
                  className="rounded"
                  onChange={(e) => handleFormChange('plot_number', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_plot_number_field"
                />
                {currentCountry === 'SA' && (
                  <small className="text-muted">{t("Must be exactly 4 digits for Saudi Arabia")}</small>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* City and State/Region - Logical geographical grouping */}
          <Row>
            <Col {...colStyle}>
              <FormGroup label={t("City/Town *")} model=".billing_address_data.city">
                <Control.text
                  updateOn="blur"
                  placeholder={t("City/Town")}
                  model=".billing_address_data.city"
                  className="mb-2"
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_city_field"
                />
              </FormGroup>
            </Col>
            <Col {...colStyle}>
              <FormGroup label={t("State/Region *")} model=".billing_address_data.region">
                <Control.text
                  updateOn="blur"
                  placeholder={t("State/Region")}
                  model=".billing_address_data.region"
                  className="mb-2"
                  onChange={(e) => handleFormChange('region', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_region_field"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Postal Code - Full width for emphasis */}
          <Row>
            <Col>
              <FormGroup label={t("Postal/Zip Code *")} model=".billing_address_data.postal_code">
                <Control.text
                  updateOn="blur"
                  placeholder={currentCountry === 'SA' ? t("Postal/Zip Code (5 digits)") : t("Postal/Zip Code")}
                  model=".billing_address_data.postal_code"
                  className="mb-2"
                  maxLength={currentCountry === 'SA' ? "5" : "10"}
                   onChange={(e) => handleFormChange('postal_code', e.target.value)}
                   autoComplete="new-password"
                   name="new_billing_postal_code_field"
                />
                {currentCountry === 'SA' && (
                  <small className="text-muted">{t("Must be exactly 5 digits for Saudi Arabia")}</small>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col {...colStyle}>
              <FormGroup label={currentCountry === 'SA' ? t("Tax Number *") : t("Tax Number")} model=".billing_address_data.vat_number">
                <Control.text
                  updateOn="blur"
                  model=".billing_address_data.vat_number"
                  placeholder={currentCountry === 'SA' ? t("Tax Number (15 digits, starts & ends with 3)") : t("Tax Number")}
                  className="mb-2"
                  maxLength={currentCountry === 'SA' ? "15" : "20"}
                  onChange={(e) => handleFormChange('vat_number', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_vat_number_field"
                />
                {currentCountry === 'SA' && (
                  <small className="text-muted">{t("Must be 15 digits, start with 3, and end with 3 for Saudi Arabia")}</small>
                )}
              </FormGroup>
            </Col>
            <Col {...colStyle}>
              <FormGroup label={currentCountry === 'SA' ? t("Registration Number *") : t("Registration Number")} model=".billing_address_data.registration_number">
                <Control.text
                  updateOn="blur"
                  model=".billing_address_data.registration_number"
                  placeholder={t("Registration Number")}
                  className="mb-2"
                  onChange={(e) => handleFormChange('registration_number', e.target.value)}
                  autoComplete="new-password"
                  name="new_billing_registration_number_field"
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default BillingContactForm;
